import { Ban, CheckCircle2, CircleHelp, Loader2, MinusCircle, ShieldAlert, XCircle } from 'lucide-react'
import clsx from 'clsx'
import {
  COMPLIANCE_COPY,
  effectiveCompliance,
  isComplianceState,
  isRunningStatus,
  stageStatusLabel,
  stepStateFor,
  type StepState,
} from '@/utils/patchStage'
import type { ComplianceState, RiskTier, VmRisk } from '@/types/patching'
import styles from './Patching.module.css'

const STATE_CLASS: Record<StepState, string> = {
  done: styles.badgeDone,
  active: styles.badgeActive,
  failed: styles.badgeFailed,
  pending: styles.badgePending,
  rejected: styles.badgeRejected,
  cancelled: styles.badgeCancelled,
}

export function StageStatusBadge({ status }: { status: string }) {
  const state = stepStateFor(status)
  return (
    <span
      className={clsx(styles.badge, STATE_CLASS[state])}
      data-testid="stage-status-badge"
      data-status={status}
      data-state={state}
    >
      {isRunningStatus(status) && <Loader2 size={11} className={styles.spin} aria-hidden="true" />}
      {stageStatusLabel(status)}
    </span>
  )
}

// ─── Compliance ───────────────────────────────────────────────────────────────

const COMPLIANCE_STYLE: Record<ComplianceState, { className: string; icon: typeof CheckCircle2 }> = {
  compliant: { className: styles.compCompliant, icon: CheckCircle2 },
  noncompliant: { className: styles.compNoncompliant, icon: XCircle },
  unverified: { className: styles.compUnverified, icon: CircleHelp },
  unsupported: { className: styles.compUnsupported, icon: MinusCircle },
}

/**
 * Only the literal `compliant` ever gets compliant styling. Anything missing or
 * unrecognised is rendered as `unverified` — never as compliant.
 */
export function ComplianceBadge({ state }: { state: ComplianceState | string | null | undefined }) {
  const effective = effectiveCompliance(state)
  const copy = COMPLIANCE_COPY[effective]
  const { className, icon: Icon } = COMPLIANCE_STYLE[effective]
  const unknownRaw = !isComplianceState(state) && state ? ` (${String(state)})` : ''
  return (
    <span
      className={clsx(styles.badge, className)}
      data-testid="compliance-badge"
      data-compliance={effective}
      title={copy.explain}
    >
      <Icon size={11} aria-hidden="true" />
      {copy.label}
      {unknownRaw}
    </span>
  )
}

// ─── Risk ─────────────────────────────────────────────────────────────────────

const TIER_CLASS: Record<RiskTier, string> = {
  low: styles.riskLow,
  medium: styles.riskMedium,
  high: styles.riskHigh,
  critical: styles.riskCritical,
}

export function RiskBadge({ risk }: { risk: Pick<VmRisk, 'risk_tier' | 'blocked'> }) {
  return (
    <span className={styles.chips}>
      <span
        className={clsx(styles.badge, TIER_CLASS[risk.risk_tier] ?? styles.badgePending)}
        data-testid="risk-tier"
        data-tier={risk.risk_tier}
      >
        <ShieldAlert size={11} aria-hidden="true" />
        {risk.risk_tier}
      </span>
      {risk.blocked && (
        <span className={clsx(styles.badge, styles.riskBlocked)} data-testid="risk-blocked">
          <Ban size={11} aria-hidden="true" />
          Blocked
        </span>
      )}
    </span>
  )
}
